import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Globe } from "lucide-react";
import { useI18n, SUPPORTED_LANGS } from "@/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ThemeToggle } from "@/components/ThemeToggle";
import { loungeCopy } from "@/lib/loungeCopy";
import { LoungeContactButton } from "@/components/lounge/LoungeContactDialog";
import logo from "@/assets/brema-logo.svg";

// Selettore lingua limitato alle lingue abilitate per la Lounge (impostabili da Admin).
function LoungeLangSwitcher({ languages }) {
  const { lang, setLang } = useI18n();
  const allowed = (languages && languages.length ? languages : SUPPORTED_LANGS.map((l) => l.code));
  const opts = SUPPORTED_LANGS.filter((l) => allowed.includes(l.code));
  useEffect(() => {
    if (allowed.length && !allowed.includes(lang)) setLang(allowed[0]);
  }, [allowed, lang, setLang]);
  return (
    <div className="flex items-center gap-1.5" data-testid="lang-switcher">
      <Globe className="hidden h-4 w-4 text-muted-foreground sm:block" />
      <Select value={allowed.includes(lang) ? lang : allowed[0]} onValueChange={setLang}>
        <SelectTrigger className="h-9 w-[104px] rounded-full border-primary bg-primary px-3 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 sm:w-[138px] sm:px-4 [&>svg]:text-primary-foreground [&>svg]:opacity-100" data-testid="lang-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {opts.map((l) => (
            <SelectItem key={l.code} value={l.code} data-testid={`lang-option-${l.code}`}>{l.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// Layout standalone per le Lounge pubbliche: header minimale (logo + lingua + tema) e
// footer semplice. NON usa il Layout globale, header o navigazione dell'app.
// Eredita il design system Brema (Dark Blu/Giallo in dark, Bianco/Bordeaux in light).
export function LoungeShell({ active, languages, children }) {
  const { lang } = useI18n();
  const c = loungeCopy(lang);
  const year = new Date().getFullYear();
  return (
    <div className="min-h-screen bg-background text-foreground antialiased" data-testid="lounge-shell">
      <header
        className="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-md"
        data-testid="lounge-header"
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3.5 sm:px-8">
          <div className="flex items-center gap-3 sm:gap-4">
            <Link to={active === "press" ? "/press-lounge" : "/ambassador-lounge"} data-testid="lounge-logo">
              <img src={logo} alt="Brema 1969" className="h-9 w-auto sm:h-10" />
            </Link>
            <a
              href="/login"
              className="whitespace-nowrap text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-foreground sm:text-sm"
              data-testid="lounge-nav-portal"
            >
              {c.nav_portal}
            </a>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <span
              className="hidden text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground md:inline lg:text-sm"
              data-testid="lounge-lang-hint"
            >
              {active === "press" ? c.nav_lang_hint_press : c.nav_lang_hint_ambassador}
            </span>
            <LoungeContactButton area={active} lang={lang} compact testidPrefix="lounge-contact" />
            <LoungeLangSwitcher languages={languages} />
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main data-testid="lounge-main">{children}</main>

      <footer className="border-t border-border bg-card/50 px-5 py-10 sm:px-8" data-testid="lounge-footer">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Brema 1969" className="h-8 w-auto opacity-90" />
            <span className="text-xs text-muted-foreground">© {year} Brema 1969. {c.footer_rights}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground">
            <LoungeContactButton area={active} lang={lang} variant="footer" testidPrefix="lounge-contact-footer" />
            <a href="mailto:press@brema-1969.com" className="hover:text-foreground" data-testid="lounge-press-email">
              {c.footer_press}: press@brema-1969.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Sezione titolo condivisa
export function LoungeSectionTitle({ eyebrow, title, subtitle, testId }) {
  return (
    <div className="mb-8 max-w-2xl" data-testid={testId}>
      {eyebrow && (
        <div className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-brand">{eyebrow}</div>
      )}
      <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{title}</h2>
      {subtitle && <p className="mt-3 text-base text-muted-foreground">{subtitle}</p>}
    </div>
  );
}
